const encoder = new TextEncoder();
const decoder = new TextDecoder();

/** Growable little-endian binary writer. */
export class BinaryWriter {
  private buf: ArrayBuffer;
  private view: DataView;
  private bytesView: Uint8Array;
  pos = 0;

  constructor(initial = 1024) {
    this.buf = new ArrayBuffer(initial);
    this.view = new DataView(this.buf);
    this.bytesView = new Uint8Array(this.buf);
  }

  reset(): this {
    this.pos = 0;
    return this;
  }

  private ensure(n: number): void {
    const need = this.pos + n;
    if (need <= this.buf.byteLength) return;
    let size = this.buf.byteLength * 2;
    while (size < need) size *= 2;
    const nb = new ArrayBuffer(size);
    new Uint8Array(nb).set(this.bytesView.subarray(0, this.pos));
    this.buf = nb;
    this.view = new DataView(nb);
    this.bytesView = new Uint8Array(nb);
  }

  u8(v: number): this {
    this.ensure(1);
    this.view.setUint8(this.pos, v);
    this.pos += 1;
    return this;
  }
  i8(v: number): this {
    this.ensure(1);
    this.view.setInt8(this.pos, v);
    this.pos += 1;
    return this;
  }
  bool(v: boolean): this {
    return this.u8(v ? 1 : 0);
  }
  u16(v: number): this {
    this.ensure(2);
    this.view.setUint16(this.pos, v, true);
    this.pos += 2;
    return this;
  }
  i16(v: number): this {
    this.ensure(2);
    this.view.setInt16(this.pos, v, true);
    this.pos += 2;
    return this;
  }
  u32(v: number): this {
    this.ensure(4);
    this.view.setUint32(this.pos, v >>> 0, true);
    this.pos += 4;
    return this;
  }
  i32(v: number): this {
    this.ensure(4);
    this.view.setInt32(this.pos, v | 0, true);
    this.pos += 4;
    return this;
  }
  f32(v: number): this {
    this.ensure(4);
    this.view.setFloat32(this.pos, v, true);
    this.pos += 4;
    return this;
  }
  f64(v: number): this {
    this.ensure(8);
    this.view.setFloat64(this.pos, v, true);
    this.pos += 8;
    return this;
  }
  /** Unsigned LEB128, valid for 0 <= v < 2^53. */
  varint(v: number): this {
    this.ensure(8);
    while (v >= 0x80) {
      this.bytesView[this.pos++] = (v % 0x80) | 0x80;
      v = Math.floor(v / 0x80);
    }
    this.bytesView[this.pos++] = v;
    return this;
  }
  /** Zig-zag signed varint. */
  svarint(v: number): this {
    return this.varint(v < 0 ? -2 * v - 1 : 2 * v);
  }
  bytes(data: Uint8Array): this {
    this.ensure(data.length);
    this.bytesView.set(data, this.pos);
    this.pos += data.length;
    return this;
  }
  /** Length-prefixed byte blob. */
  blob(data: Uint8Array): this {
    this.varint(data.length);
    return this.bytes(data);
  }
  string(s: string): this {
    return this.blob(encoder.encode(s));
  }
  /** Overwrite a u16 at an earlier position (for back-patching counts). */
  patchU16(at: number, v: number): void {
    this.view.setUint16(at, v, true);
  }
  patchU32(at: number, v: number): void {
    this.view.setUint32(at, v >>> 0, true);
  }
  /** Copy of the written bytes. */
  finish(): Uint8Array {
    return this.bytesView.slice(0, this.pos);
  }
  /** View of the written bytes (invalidated by further writes). */
  view8(): Uint8Array {
    return this.bytesView.subarray(0, this.pos);
  }
}

export class BinaryReader {
  private view: DataView;
  pos = 0;

  constructor(readonly data: Uint8Array) {
    this.view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  }

  get remaining(): number {
    return this.data.byteLength - this.pos;
  }

  u8(): number {
    return this.view.getUint8(this.pos++);
  }
  i8(): number {
    return this.view.getInt8(this.pos++);
  }
  bool(): boolean {
    return this.u8() !== 0;
  }
  u16(): number {
    const v = this.view.getUint16(this.pos, true);
    this.pos += 2;
    return v;
  }
  i16(): number {
    const v = this.view.getInt16(this.pos, true);
    this.pos += 2;
    return v;
  }
  u32(): number {
    const v = this.view.getUint32(this.pos, true);
    this.pos += 4;
    return v;
  }
  i32(): number {
    const v = this.view.getInt32(this.pos, true);
    this.pos += 4;
    return v;
  }
  f32(): number {
    const v = this.view.getFloat32(this.pos, true);
    this.pos += 4;
    return v;
  }
  f64(): number {
    const v = this.view.getFloat64(this.pos, true);
    this.pos += 8;
    return v;
  }
  varint(): number {
    let result = 0;
    let mul = 1;
    for (;;) {
      const b = this.data[this.pos++];
      if (b === undefined) throw new RangeError('varint past end of buffer');
      result += (b & 0x7f) * mul;
      if (b < 0x80) return result;
      mul *= 0x80;
    }
  }
  svarint(): number {
    const v = this.varint();
    return v % 2 === 1 ? -(v + 1) / 2 : v / 2;
  }
  bytes(n: number): Uint8Array {
    if (this.pos + n > this.data.byteLength) throw new RangeError('read past end of buffer');
    const out = this.data.subarray(this.pos, this.pos + n);
    this.pos += n;
    return out;
  }
  blob(): Uint8Array {
    return this.bytes(this.varint());
  }
  string(): string {
    return decoder.decode(this.blob());
  }
}
